import { Box } from "@mui/material"
import type { FC } from "react"
import { useEffect } from "react"
import type { Control, UseFormSetValue } from "react-hook-form"
import { useWatch } from "react-hook-form"

import { LABEL_TYPE_OPTIONS } from "../../constants/materialRequirements"
import type { MaterialRequirementsFormValues } from "../../types"

import { FormBooleanRadioGroup, FormMultiSelect, FormNumericField } from "@/components/UiKit/FormControls"

interface MaterialRequirementsFormProps {
  control: Control<MaterialRequirementsFormValues>
  setValue: UseFormSetValue<MaterialRequirementsFormValues>
}

export const MaterialRequirementsForm: FC<MaterialRequirementsFormProps> = ({ control, setValue }) => {
  const shelfLifeRequired = useWatch({
    control,
    name: "shelfLifeRequired",
  })

  // Clear shelf life requirement amount when user switches to "No"
  useEffect(() => {
    if (!shelfLifeRequired) {
      setValue("shelfLifeRequirement", undefined, { shouldDirty: true })
    }
  }, [shelfLifeRequired, setValue])

  return (
    <Box className='space-y-6'>
      <Box className='grid grid-cols-2 gap-6'>
        <FormBooleanRadioGroup
          control={control}
          name='allowsRecertifiedMaterial'
          label='Allows Recertified Material'
          variant='yes-no'
          data-testid='allows-recertified-material-radio'
        />

        <FormBooleanRadioGroup
          control={control}
          name='prefersNewestMaterial'
          label='Prefers Newest Material'
          variant='yes-no'
          data-testid='prefers-newest-material-radio'
        />
      </Box>

      <Box className='grid grid-cols-2 gap-6'>
        <FormBooleanRadioGroup
          control={control}
          name='shelfLifeRequired'
          label='Shelf Life Requirement'
          variant='yes-no'
          data-testid='shelf-life-required-radio'
        />

        <FormNumericField
          control={control}
          name='shelfLifeRequirement'
          label='Shelf Life Requirement Amount (%)'
          placeholder='Enter percentage'
          allowNegative={false}
          disabled={!shelfLifeRequired}
          data-testid='shelf-life-requirement-input'
        />
      </Box>

      <Box className='grid grid-cols-2 gap-6'>
        <FormBooleanRadioGroup
          control={control}
          name='changeApprovalRequired'
          label='Change Approval Required'
          variant='yes-no'
          data-testid='change-approval-required-radio'
        />

        <FormBooleanRadioGroup
          control={control}
          name='prefersFewestLots'
          label='Prefers Fewest Lots'
          variant='yes-no'
          data-testid='prefers-fewest-lots-radio'
        />
      </Box>

      <FormMultiSelect
        control={control}
        name='labelConfiguration'
        label='Shipping Label'
        placeholder='Select...'
        options={LABEL_TYPE_OPTIONS}
        data-testid='label-configuration-select'
      />
    </Box>
  )
}
